

import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useBadges } from '@/contexts/BadgesContext';
import { UnreadBadge } from './UnreadBadge';
import { useQuery } from '@tanstack/react-query';
import { baseNavItems } from '@/config/navigationConfig';

const NavItems = ({ isMobile = false, onItemClick }: { isMobile?: boolean; onItemClick?: () => void }) => {
  const location = useLocation();
  const { profileData } = useAuth();
  const { messagesCount: unreadMessagesCount, notificationsCount } = useBadges();

  // Check roles via single getUserRoles call
  const { data: userRoles = { isAdmin: false, isModerator: false, role: 'user' } } = useQuery({
    queryKey: ['userRoles', profileData?.id],
    queryFn: async () => {
      if (!profileData?.id) return { isAdmin: false, isModerator: false, role: 'user' };
      const { AdminService } = await import('@/core/services/AdminService');
      return await AdminService.getUserRoles(profileData.id);
    },
    enabled: !!profileData?.id,
    staleTime: 5 * 60 * 1000
  });

  const isAdmin = userRoles.isAdmin;
  const isModerator = userRoles.isModerator;
  const isSponsor = profileData?.role === 'sponsor';

  // Build navItems from baseNavItems with conditional logic
  const navItems = baseNavItems
    .filter(item => {
      // Mobile only shows first 4 items (Feed, Chats, Likes, Notifications)
      if (isMobile) {
        return ['Feed', 'Chats', 'Likes', 'Notificações'].includes(item.label);
      }
      
      // Desktop: filter based on role
      if (item.label === 'Painel Admin' && !isAdmin && !isModerator) return false;
      
      return true;
    })
    .map(item => ({
      ...item,
      // Add badge counts
      badgeCount: item.badgeKey === 'messages' 
        ? (unreadMessagesCount > 0 ? unreadMessagesCount : undefined)
        : item.badgeKey === 'notifications'
        ? (notificationsCount > 0 ? notificationsCount : undefined)
        : undefined,
      // Customize admin label/description
      label: item.label === 'Painel Admin' 
        ? (isAdmin ? 'Painel do Administrador' : 'Painel do Moderador')
        : item.label,
      description: item.label === 'Painel Admin'
        ? (isAdmin ? 'Controle total da plataforma' : 'Gestão de usuários e conteúdo')
        : item.description
    }));

  return (
    <nav className={cn(
      isMobile ? "grid grid-cols-4 gap-0 px-2" : "space-y-1 px-3"
    )}>
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path || 
          (item.path === '/main/chats' && location.pathname.startsWith('/main/chat/'));
        
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onItemClick}
            className={cn(
              "flex transition-all duration-200 group relative",
              isMobile 
                ? "flex-col items-center gap-1 py-2 rounded-lg w-full px-1" 
                : "items-center md:gap-2 lg:gap-3 md:px-2 md:py-2 lg:px-3 lg:py-2.5 rounded-lg",
              isActive 
                ? "bg-luxury-pinkBright text-white shadow-lg" 
                : "text-gray-300 hover:text-white hover:bg-white/5"
            )}
          >
            <div className="relative">
              <Icon 
                className={cn(
                  "flex-shrink-0 transition-transform duration-200",
                  "md:w-4 md:h-4 lg:w-5 lg:h-5",
                  isActive ? "scale-110" : "group-hover:scale-105"
                )} 
              />
              {item.badgeCount && (
                <UnreadBadge 
                  count={item.badgeCount} 
                  size="sm"
                />
              )}
            </div>
            {isMobile ? (
              <div className="text-xs font-medium truncate text-center">
                {item.label.split(' ')[0]}
              </div>
            ) : (
              <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">
                {item.label}
              </div>
              <div className={cn(
                "text-[10px] truncate",
                isActive ? "text-white" : "text-gray-400"
              )}>
                  {item.description}
                </div>
              </div>
            )}
          </Link>
        );
      })}
    </nav>
  );
};

export default NavItems;

